/**
 * PersonaProvider — React context provider for persona-adaptive UX.
 * Story 13.6: Persona-Adaptive Density System (AC-2)
 *
 * Makes PersonaContext available to all components via usePersonaContext hook.
 */

import { createContext, useContext, useMemo } from "react";
import { useLocation } from "react-router";
import { useAuthStore } from "@/shared/stores/auth-store";
import { useDensityStore } from "@/shared/store/density-store";
import { derivePersonaContext, type PersonaContext } from "./persona-context";

const PersonaReactContext = createContext<PersonaContext | null>(null);

function PersonaProvider({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const userRole = useAuthStore((s) => s.user?.role ?? "data-engineer");
  const densityOverride = useDensityStore((s) => s.densityOverride);

  const context = useMemo(
    () => derivePersonaContext(userRole, pathname, densityOverride),
    [userRole, pathname, densityOverride],
  );

  return (
    <PersonaReactContext.Provider value={context}>
      {children}
    </PersonaReactContext.Provider>
  );
}

function usePersonaContext(): PersonaContext {
  const context = useContext(PersonaReactContext);
  if (!context) {
    throw new Error("usePersonaContext must be used within a PersonaProvider");
  }
  return context;
}

export { PersonaProvider, usePersonaContext, PersonaReactContext };
